import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';

type UserType = 'student' | 'landlord' | 'admin';
type Institution = 'university_of_ghana' | 'kwame_nkrumah_university' | 'university_of_cape_coast' | 'ghana_institute_of_management' | 'university_of_professional_studies' | 'central_university' | 'ashesi_university' | 'other';

interface Profile {
  id: string;
  user_id: string;
  full_name: string;
  user_type: UserType;
  phone_number?: string | null;
  institution?: Institution | null;
  program?: string | null;
  student_id?: string | null;
  business_name?: string | null;
  avatar_url?: string | null;
  verification_status?: string | null;
  preferred_school?: string | null;
  created_at: string;
  updated_at: string;
}

interface UserData {
  full_name: string;
  user_type: UserType;
  phone_number?: string;
  institution?: Institution;
  program?: string;
  student_id?: string;
  business_name?: string;
}

// Track admin status separately via user_roles table

interface AuthError {
  message: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isHydrated: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string, userData: UserData) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  resendConfirmation: (email: string) => Promise<{ error: AuthError | null }>;
  getDashboardUrl: () => string;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { toast } = useToast();
  
  // Use refs to track current state and prevent race conditions
  const currentUserId = useRef<string | null>(null);
  const profileFetchInProgress = useRef<boolean>(false);
  const profileRef = useRef<Profile | null>(null);
  const hydratedRef = useRef(false);

  const checkAdminRole = async (userId: string) => {
    try {
      const { data } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'admin'
      });
      setIsAdmin(data === true);
    } catch {
      setIsAdmin(false);
    }
  };

  const fetchProfile = async (userId: string) => {
    // Skip if already fetching or if user changed
    if (profileFetchInProgress.current || currentUserId.current !== userId) {
      return;
    }

    profileFetchInProgress.current = true;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      // Check if user is still the same after fetch
      if (currentUserId.current !== userId) {
        return;
      }

      if (error) {
        if (error.code === 'PGRST116') {
          setProfile(null);
          profileRef.current = null;
          return;
        }
        console.error('Error fetching profile:', error);
        setProfile(null);
        profileRef.current = null;
        return;
      }

      setProfile(data);
      profileRef.current = data;
    } catch (error) {
      console.error('Error in fetchProfile:', error);
      if (currentUserId.current === userId) {
        setProfile(null);
        profileRef.current = null;
      }
    } finally {
      profileFetchInProgress.current = false;
      if (currentUserId.current && !hydratedRef.current) {
        hydratedRef.current = true;
        setLoading(false);
        setIsHydrated(true);
      }
    }
  };

  const refreshProfile = useCallback(async () => {
    if (user) {
      profileFetchInProgress.current = false; // Reset the lock
      await fetchProfile(user.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    let mounted = true;

    // Safety timeout: force hydration after 5 seconds even if Supabase hangs
    const safetyTimer = setTimeout(() => {
      if (mounted && !hydratedRef.current) {
        console.warn('[Auth] Safety timeout — forcing hydrated state');
        hydratedRef.current = true;
        setLoading(false);
        setIsHydrated(true);
      }
    }, 5000);

    /**
     * Attempt to read the session directly from localStorage as a fallback
     * when the Navigator LockManager lock times out. Supabase stores the
     * session under the key "sb-<project-ref>-auth-token" in localStorage.
     */
    const getSessionFromStorage = (): Session | null => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? "https://brkzsfbiuazrbmpnpzyl.supabase.co";
        const storageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Supabase stores { access_token, refresh_token, expires_at, user, ... }
        if (parsed?.access_token && parsed?.user) {
          return parsed as Session;
        }
      } catch {
        // Ignore storage read errors
      }
      return null;
    };

    const initializeAuth = async () => {
      try {
        console.log('[Auth] initializeAuth started');
        // First, get the current session
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (initialSession?.user) {
          console.log('[Auth] Session found, fetching profile…');
          currentUserId.current = initialSession.user.id;
          setSession(initialSession);
          setUser(initialSession.user);
          // Fetch profile and admin role in parallel
          await Promise.all([
            fetchProfile(initialSession.user.id),
            checkAdminRole(initialSession.user.id)
          ]);
        } else {
          console.log('[Auth] No session found — guest mode');
        }
        
        if (!hydratedRef.current) {
          hydratedRef.current = true;
          setLoading(false);
          setIsHydrated(true);
        }
        console.log('[Auth] initializeAuth complete');
      } catch (error) {
        // Handle the Navigator LockManager timeout gracefully. This happens when
        // heavy resource loading blocks the main thread long enough that the
        // internal Supabase lock (used for cross-tab token sync) cannot be
        // acquired within its 10 s window. We fall back to reading the session
        // directly from localStorage so the user doesn't get logged out and the
        // exception is not reported as unhandled.
        const isLockTimeout =
          error instanceof Error &&
          (error.name === 'NavigatorLockAcquireTimeoutError' ||
            error.message.includes('Navigator LockManager lock') ||
            error.message.includes('timed out'));

        if (isLockTimeout) {
          console.warn('[Auth] Navigator lock timed out — falling back to localStorage session');
          if (mounted) {
            const fallbackSession = getSessionFromStorage();
            if (fallbackSession?.user) {
              currentUserId.current = fallbackSession.user.id;
              setSession(fallbackSession);
              setUser(fallbackSession.user);
              // Kick off profile + admin fetch without awaiting so we unblock hydration
              setTimeout(() => {
                fetchProfile(fallbackSession.user.id);
                checkAdminRole(fallbackSession.user.id);
              }, 0);
            }
          }
        } else {
          console.error('[Auth] Error initializing auth:', error);
        }

        if (mounted && !hydratedRef.current) {
          hydratedRef.current = true;
          setLoading(false);
          setIsHydrated(true);
        }
      }
    };

    // Initialize auth state
    initializeAuth();

    // Set up auth state listener for subsequent changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;
        
        // Auth state change (do not log user id in production)

        // Handle different auth events
        if (event === 'SIGNED_OUT') {
          currentUserId.current = null;
          profileRef.current = null;
          setSession(null);
          setUser(null);
          setProfile(null);
          setIsAdmin(false);
          return;
        }

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (newSession?.user) {
            // Use ref to avoid stale closure — profile state is always null in this callback
            const hasProfile = !!profileRef.current;
            // If user ID changed, or if we don't have a profile yet for the current user
            if (currentUserId.current !== newSession.user.id || (!hasProfile && !profileFetchInProgress.current)) {
              currentUserId.current = newSession.user.id;
              setSession(newSession);
              setUser(newSession.user);
              // Fetch profile and admin role
              setTimeout(() => {
                fetchProfile(newSession.user.id);
                checkAdminRole(newSession.user.id);
              }, 0);
            } else {
              // Same user with profile already loaded, just update session (e.g. token refresh)
              setSession(newSession);
              setUser(newSession.user);
            }
          }
        }

        // Ensure hydrated after any auth change (idempotent via ref guard)
        if (!hydratedRef.current) {
          if (!newSession?.user || profileRef.current) {
            hydratedRef.current = true;
            setLoading(false);
            setIsHydrated(true);
          }
        }
        // If we have a user but no profile yet, fetchProfile will handle setting loading=false in its finally block
      }
    );

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; profile updates handled by subscription
  }, []);

  const signUp = useCallback(async (email: string, password: string, userData: UserData) => {
    const redirectUrl = `${window.location.origin}/email-confirmation`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: userData
      }
    });

    if (error) {
      let errorMessage = error.message;

      // Provide more user-friendly error messages
      if (error.message.includes('User already registered')) {
        errorMessage = 'An account with this email already exists. Please sign in instead or use a different email.';
      } else if (error.message.includes('Invalid email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.message.includes('Password should be at least')) {
        errorMessage = 'Password must be at least 6 characters long.';
      }

      toast({
        title: "Signup Error",
        description: errorMessage,
        variant: "destructive"
      });
    } else {
      toast({
        title:"Account Created Successfully!",
        description: "Please check your email and click the confirmation link to activate your account.",
      });
    }

    return { error };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      let errorMessage = error.message;

      // Provide more user-friendly error messages
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = 'Please confirm your email address before signing in. Check your inbox for a confirmation link.';
      } else if (error.message.includes('Too many requests')) {
        errorMessage = 'Too many login attempts. Please wait a moment and try again.';
      }

      toast({
        title: "Login Error",
        description: errorMessage,
        variant: "destructive"
      });
    } else if (data.user) {
      // Immediately set user state to prevent flicker
      currentUserId.current = data.user.id;
      setUser(data.user);
      setSession(data.session);
      
      toast({
        title:"Welcome back!",
        description: "You have successfully signed in to your account.",
      });
    }

    return { error };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  const signOut = useCallback(async () => {
    // Clear refs and state first
    currentUserId.current = null;
    profileFetchInProgress.current = false;
    
    setUser(null);
    setSession(null);
    setProfile(null);

    // Clear all Supabase local storage keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) {
        localStorage.removeItem(key);
      }
    });

    // Clear session storage as well
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('sb-')) {
        sessionStorage.removeItem(key);
      }
    });

    try {
      // Sign out from Supabase (ignore errors since we've already cleared local state)
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.error('Error during sign out:', e);
    }

    // Force redirect to home page (public route)
    window.location.href = '/';
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Password reset sent",
        description: "Check your email for password reset instructions.",
      });
    }

    return { error };
  }, [toast]);

  const resendConfirmation = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: `${window.location.origin}/email-confirmation`
      }
    });

    if (error) {
      let errorMessage = error.message;

      if (error.message.includes('For security purposes')) {
        errorMessage = 'Please wait a moment before requesting another confirmation email.';
      } else if (error.message.includes('User not found')) {
        errorMessage = 'No account found with this email address. Please sign up first.';
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } else {
      toast({
        title:"Confirmation Email Sent!",
        description: "Please check your email for the new confirmation link.",
      });
    }

    return { error };
  }, [toast]);

  const getDashboardUrl = useCallback(() => {
    if (isAdmin) return '/admin';
    if (!profile?.user_type) return '/';
    switch (profile.user_type) {
      case 'student':
        return '/';
      case 'landlord':
        return '/landlord-dashboard';
      default:
        return '/';
    }
  }, [isAdmin, profile?.user_type]);

  const value = React.useMemo(() => ({
    user,
    session,
    profile,
    loading,
    isHydrated,
    isAdmin,
    signUp,
    signIn,
    signOut,
    resetPassword,
    resendConfirmation,
    getDashboardUrl,
    refreshProfile
  }), [user, session, profile, loading, isHydrated, isAdmin, signUp, signIn, signOut, resetPassword, resendConfirmation, getDashboardUrl, refreshProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
